import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS, TACOVERSE_URL } from "../data/brand";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-bv-line bg-white/90 backdrop-blur-xl" data-testid="site-header">
        <div className="container-x flex h-16 items-center justify-between gap-6 md:h-[72px]">
          <Link to="/" aria-label="Barverse — home" data-testid="nav-logo">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-mono text-[11px] font-bold uppercase tracking-[0.22em] transition-colors duration-200 ${
                    isActive ? "text-bv-ink underline decoration-2 underline-offset-8" : "text-bv-flame hover:text-bv-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <a
            href="https://tacoverse.com"
            className="hidden items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-bv-flame transition-colors hover:text-bv-ink xl:flex"
            data-testid="nav-tacoverse-backlink"
          >
            <img src="/images/tacosun.png" alt="" className="h-3.5 w-auto" aria-hidden="true" />
            Tacoverse.com
            <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
          </a>

          <div className="hidden lg:block">
            <Link to="/locations" className="btn-primary !px-6 !py-3" data-testid="nav-cta">
              Find a Barverse
            </Link>
          </div>

          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-bv-line bg-bv-panel text-bv-ink transition-colors hover:border-bv-flame lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 flex flex-col bg-white pt-24 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
          >
            <nav className="container-x flex-1 overflow-y-auto" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.06, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.path}
                    data-testid={`mobile-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block border-b border-bv-line py-4 font-display text-4xl uppercase ${
                        isActive ? "text-bv-flame" : "text-bv-ink"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              className="container-x pb-10 pt-6"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
            >
              <Link to="/locations" className="btn-primary w-full" data-testid="mobile-cta">
                Find a Barverse
              </Link>
              <p className="mt-6 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-bv-sub">
                Food by{" "}
                <a href={TACOVERSE_URL} className="text-tcv-yellow" data-testid="mobile-tacoverse-link">
                  Tacoverse
                  <ArrowUpRight className="ml-0.5 inline h-3 w-3" aria-hidden="true" />
                </a>
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
